import { useState, useEffect, useMemo } from 'react'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { Plus, Loader2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { CreateProjectForm } from './CreateProjectForm'
import { EditProjectDialog } from './EditProjectDialog'
import { PortfolioSummary } from './PortfolioSummary'
import { PortfolioGrid, groupProjects } from './PortfolioGrid'
import {
  PortfolioToolbar,
  EMPTY_FILTERS,
  type PortfolioFilters,
  type GroupBy,
} from './PortfolioToolbar'
import { EMPTY_METADATA, type MetadataValues } from './ProjectMetadataFields'
import type { Project } from '@/lib/api/client'

interface ProjectSelectorProps {
  onProjectSelected: () => void
}

interface CreateState {
  open: boolean
  name: string
  slug: string
  description: string
  metadata: MetadataValues
  error: string
}

const INITIAL_CREATE: CreateState = {
  open: false,
  name: '',
  slug: '',
  description: '',
  metadata: EMPTY_METADATA,
  error: '',
}

function applyFilters(projects: Project[], filters: PortfolioFilters): Project[] {
  const q = filters.search.trim().toLowerCase()
  return projects.filter((p) => {
    if (q) {
      const haystack = [p.name, p.slug, p.description ?? '', p.industry ?? '', p.clientName ?? '']
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(q)) return false
    }
    if (filters.companyType && p.companyType !== filters.companyType) return false
    if (filters.market && p.market !== filters.market) return false
    if (filters.productType && p.productType !== filters.productType) return false
    return true
  })
}

export function ProjectSelector({ onProjectSelected }: ProjectSelectorProps) {
  const [createState, setCreateState] = useState<CreateState>(INITIAL_CREATE)
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [editProjectId, setEditProjectId] = useState<string | null>(null)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [filters, setFilters] = useState<PortfolioFilters>(EMPTY_FILTERS)
  const [groupBy, setGroupBy] = useState<GroupBy>('none')

  const { user, logout, currentAccount } = useAuthStore()
  const {
    projects,
    stats,
    summary,
    isLoading,
    error,
    loadPortfolio,
    selectProject,
    createProject,
    updateProject,
    deleteProject,
    clearError,
  } = useProjectStore()

  useEffect(() => {
    if (currentAccount) loadPortfolio(currentAccount.id)
  }, [currentAccount, loadPortfolio])

  useEffect(() => {
    const slug = createState.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    setCreateState((s) => ({ ...s, slug }))
  }, [createState.name])

  const filtered = useMemo(() => applyFilters(projects, filters), [projects, filters])
  const groups = useMemo(() => groupProjects(filtered, groupBy), [filtered, groupBy])

  const handleSelectProject = async (projectId: string) => {
    if (!currentAccount) return
    try {
      await selectProject(currentAccount.id, projectId)
      onProjectSelected()
    } catch {
      // store handles error
    }
  }

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreateState((s) => ({ ...s, error: '' }))
    clearError()
    if (!currentAccount) return setCreateState((s) => ({ ...s, error: 'No account selected' }))
    if (!createState.slug) return setCreateState((s) => ({ ...s, error: 'Please enter a project name' }))
    const m = createState.metadata
    try {
      await createProject(currentAccount.id, {
        name: createState.name,
        slug: createState.slug,
        description: createState.description || undefined,
        companyType: m.companyType ?? undefined,
        productType: m.productType ?? undefined,
        market: m.market ?? undefined,
        industry: m.industry.trim() || undefined,
        clientName: m.clientName.trim() || undefined,
      })
      onProjectSelected()
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to create project'
      setCreateState((s) => ({ ...s, error }))
    }
  }

  const handleSaveEdit = async (updates: Parameters<typeof updateProject>[2]) => {
    if (!currentAccount || !editProjectId) return
    setIsSavingEdit(true)
    try {
      await updateProject(currentAccount.id, editProjectId, updates)
      setEditProjectId(null)
      // Refresh stats/summary after edit
      await loadPortfolio(currentAccount.id)
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleDeleteProject = async () => {
    if (!currentAccount || !deleteProjectId) return
    setIsDeleting(true)
    try {
      await deleteProject(currentAccount.id, deleteProjectId)
      setDeleteProjectId(null)
      await loadPortfolio(currentAccount.id)
    } finally {
      setIsDeleting(false)
    }
  }

  const projectToDelete = projects.find((p) => p.id === deleteProjectId)
  const projectToEdit = projects.find((p) => p.id === editProjectId) ?? null
  const firstName = user?.name?.split(' ')[0] ?? user?.name

  if (isLoading && projects.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-warm-100 rounded-2xl">
        <Loader2 className="w-6 h-6 text-accent-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full w-full bg-warm-100 rounded-2xl overflow-auto">
      <div className="max-w-6xl mx-auto px-10 py-10">
        <header className="flex items-end justify-between mb-7">
          <div>
            <h1 className="font-display text-[28px] font-semibold text-text leading-[1.1] tracking-tight">
              Your projects
            </h1>
            <p className="text-sm text-text-secondary mt-1.5">Welcome back, {firstName}</p>
          </div>
          <button
            onClick={() => logout()}
            className="text-sm text-text-muted hover:text-text transition-colors"
          >
            Sign out
          </button>
        </header>

        {projects.length > 0 && (
          <PortfolioSummary summary={summary} stats={stats} />
        )}

        {error && (
          <div className="mt-6 px-3 py-2 bg-danger-bg text-danger-fg text-sm rounded-lg">
            {error}
          </div>
        )}

        {createState.open ? (
          <div className="mt-7">
            <CreateProjectForm
              name={createState.name}
              slug={createState.slug}
              description={createState.description}
              metadata={createState.metadata}
              error={createState.error}
              isLoading={isLoading}
              onNameChange={(v) => setCreateState((s) => ({ ...s, name: v }))}
              onSlugChange={(v) => setCreateState((s) => ({ ...s, slug: v }))}
              onDescriptionChange={(v) => setCreateState((s) => ({ ...s, description: v }))}
              onMetadataChange={(metadata) => setCreateState((s) => ({ ...s, metadata }))}
              onSubmit={handleCreateProject}
              onCancel={() => setCreateState(INITIAL_CREATE)}
            />
          </div>
        ) : projects.length > 0 ? (
          <div className="mt-7 space-y-5">
            <PortfolioToolbar
              filters={filters}
              onFiltersChange={setFilters}
              groupBy={groupBy}
              onGroupByChange={setGroupBy}
              onCreate={() => setCreateState((s) => ({ ...s, open: true }))}
            />

            <PortfolioGrid
              filtered={filtered}
              groups={groups}
              groupBy={groupBy}
              stats={stats}
              onOpen={handleSelectProject}
              onEdit={setEditProjectId}
              onDelete={setDeleteProjectId}
            />
          </div>
        ) : (
          <div className="mt-12 py-12 px-6 text-center bg-surface border border-dashed border-border rounded-xl">
            <p className="font-display text-lg font-semibold text-text">No projects yet</p>
            <p className="text-sm text-text-muted mt-1 mb-5">
              Create your first project to start working with Skerpa.
            </p>
            <button
              onClick={() => setCreateState((s) => ({ ...s, open: true }))}
              className="inline-flex items-center gap-1.5 h-9 px-4 bg-accent-500 text-white text-sm font-medium rounded-lg hover:bg-accent-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New project
            </button>
          </div>
        )}
      </div>

      <EditProjectDialog
        project={projectToEdit}
        isSaving={isSavingEdit}
        onSave={handleSaveEdit}
        onClose={() => setEditProjectId(null)}
      />

      <AlertDialog
        open={!!deleteProjectId}
        onOpenChange={(open) => !open && setDeleteProjectId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <strong>{projectToDelete?.name}</strong>. All ontology data,
              customers, and contacts in this project will be lost. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={isDeleting}
              className="bg-danger-fg hover:bg-danger-fg/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete project'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
